import { useEffect, useMemo, useRef, useState } from "react";
import {
  Shield,
  Users as UsersIcon,
  Key,
  RefreshCw,
  Plus,
  Check,
  X,
  UserCheck,
  Trash2,
  Send,
  ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";
import { ACTION_KEYS } from "../constants/actions";
import { useAuthStore } from "../stores/authStore";

interface ApiUser {
  id: string;
  email: string;
  displayName?: string;
  avatarUrl?: string;
  roles: {
    id: string;
    name: string;
    description?: string;
    actions: {
      id: string;
      key: string;
      description: string;
    }[];
  }[];
  permissions: string[];
  lastLoginAt?: string;
  createdAt?: string;
}

interface ApiRole {
  id: string;
  name: string;
  description?: string;
  actions: {
    id: string;
    key: string;
    description: string;
  }[];
}

interface ApiAction {
  id: string;
  key: string;
  description: string;
}

interface ApiTeamMember {
  id: string;
  firstName: string;
  lastName: string;
  role?: string;
}

interface PccAccount {
  id: string;
  email: string;
  displayName?: string;
  teamMemberId: string;
  isActive: boolean;
  createdAt: string;
}

async function authenticatedRequest<T>(
  input: RequestInfo,
  init: RequestInit = {}
): Promise<T> {
  const response = await fetch(input, {
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
    credentials: "include",
    ...init,
  });

  if (response.status === 204) {
    return {} as T;
  }

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = (data && (data.message || data.error)) || "Request failed";
    throw new Error(message);
  }

  return data as T;
}

interface SelectOption {
  value: string;
  label: string;
}

function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Select...",
  required,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? "";

  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleSelect = (option: SelectOption) => {
    onChange(option.value);
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setQuery("");
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Hidden native select for form required validation */}
      {required && (
        <select
          tabIndex={-1}
          required
          value={value}
          onChange={() => {}}
          className="absolute inset-0 opacity-0 pointer-events-none w-full"
          aria-hidden
        >
          <option value="" />
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}

      <button
        type="button"
        onClick={() => { setOpen((prev) => !prev); setQuery(""); }}
        className="mt-1 flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <span className={value ? "text-gray-900 truncate" : "text-gray-400"}>
          {value ? selectedLabel : placeholder}
        </span>
        <span className="flex items-center gap-1 ml-2 shrink-0">
          {value && (
            <span
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">No results</li>
            ) : (
              filtered.map((option) => (
                <li
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  className={`cursor-pointer px-3 py-2 text-sm hover:bg-indigo-50 hover:text-indigo-700 ${
                    option.value === value ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-800"
                  }`}
                >
                  {option.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export function AccessControl() {
  const { hasPermission, user } = useAuthStore((state) => ({
    hasPermission: state.hasPermission,
    user: state.user,
  }));
  const allowed = hasPermission(ACTION_KEYS.ACCESS_MANAGE);
  const isAdmin = user?.roles?.some((r) => r.name === "admin") ?? false;

  const [users, setUsers] = useState<ApiUser[]>([]);
  const [roles, setRoles] = useState<ApiRole[]>([]);
  const [actions, setActions] = useState<ApiAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [newUserRoleIds, setNewUserRoleIds] = useState<string[]>([]);
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newActionKey, setNewActionKey] = useState("");
  const [newActionDescription, setNewActionDescription] = useState("");
  const [creatingAction, setCreatingAction] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");
  const [newRoleActionKeys, setNewRoleActionKeys] = useState<string[]>([]);
  const [creatingRole, setCreatingRole] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [editingRoleDescription, setEditingRoleDescription] = useState("");
  const [editingRoleActionKeys, setEditingRoleActionKeys] = useState<string[]>(
    []
  );
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null);

  const [teamMembers, setTeamMembers] = useState<ApiTeamMember[]>([]);
  const [pccAccounts, setPccAccounts] = useState<PccAccount[]>([]);
  const [newPccEmail, setNewPccEmail] = useState("");
  const [newPccTeamMemberId, setNewPccTeamMemberId] = useState("");
  const [creatingPcc, setCreatingPcc] = useState(false);
  const [deletingPccId, setDeletingPccId] = useState<string | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [confirmDeleteUserId, setConfirmDeleteUserId] = useState<string | null>(null);

  // Link-existing-user state
  const [linkUserId, setLinkUserId] = useState("");
  const [linkTeamMemberId, setLinkTeamMemberId] = useState("");
  const [linkingUser, setLinkingUser] = useState(false);
  const [unlinkingUserId, setUnlinkingUserId] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, rolesRes, actionsRes, membersRes, pccRes] = await Promise.all([
        authenticatedRequest<{ users: ApiUser[] }>("/api/v1/auth/users"),
        authenticatedRequest<{ roles: ApiRole[] }>("/api/v1/auth/roles"),
        authenticatedRequest<{ actions: ApiAction[] }>("/api/v1/auth/actions"),
        authenticatedRequest<{ success: boolean; data: ApiTeamMember[] }>("/api/v1/team-members"),
        authenticatedRequest<{ accounts: PccAccount[] }>("/api/v1/access-control/pcc-accounts"),
      ]);
      setUsers(usersRes.users ?? []);
      setRoles(rolesRes.roles ?? []);
      setActions(actionsRes.actions ?? []);
      setTeamMembers(membersRes.data ?? []);
      setPccAccounts(pccRes.accounts ?? []);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load access data";
      toast.error(message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (allowed) {
      void loadData();
    }
  }, [allowed]);

  const actionsByKey = useMemo(() => {
    const map = new Map<string, ApiAction>();
    actions.forEach((action) => map.set(action.key, action));
    return map;
  }, [actions]);

  // Users whose account is already linked to a team member (have teamMemberId)
  const linkedAccountIds = useMemo(
    () => new Set(pccAccounts.map((a) => a.id)),
    [pccAccounts]
  );

  // Platform users not yet linked — candidates for the "link existing user" form
  const unlinkableUsers = useMemo(
    () => users.filter((u) => !linkedAccountIds.has(u.id)),
    [users, linkedAccountIds]
  );

  // Team members not yet linked to any account
  const unlinkedTeamMembers = useMemo(
    () => teamMembers.filter((m) => !pccAccounts.some((a) => a.teamMemberId === m.id)),
    [teamMembers, pccAccounts]
  );

  const handleRefresh = () => {
    setIsRefreshing(true);
    void loadData();
  };

  const toggleNewRoleAction = (actionKey: string) => {
    setNewRoleActionKeys((prev) =>
      prev.includes(actionKey)
        ? prev.filter((key) => key !== actionKey)
        : [...prev, actionKey]
    );
  };

  const startEditingRole = (role: ApiRole) => {
    setEditingRoleId(role.id);
    setEditingRoleDescription(role.description || "");
    setEditingRoleActionKeys(role.actions.map((action) => action.key));
  };

  const cancelEditingRole = () => {
    setEditingRoleId(null);
    setEditingRoleDescription("");
    setEditingRoleActionKeys([]);
    setSavingRoleId(null);
  };

  const toggleEditingRoleAction = (actionKey: string) => {
    setEditingRoleActionKeys((prev) =>
      prev.includes(actionKey)
        ? prev.filter((key) => key !== actionKey)
        : [...prev, actionKey]
    );
  };

  const saveRoleUpdates = async (roleId: string) => {
    setSavingRoleId(roleId);
    try {
      const response = await authenticatedRequest<{ role: ApiRole }>(
        `/api/v1/access-control/roles/${roleId}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            description: editingRoleDescription.trim(),
            actionKeys: editingRoleActionKeys,
          }),
        }
      );
      setRoles((prev) =>
        prev.map((role) => (role.id === roleId ? response.role : role))
      );
      toast.success("Role updated");
      cancelEditingRole();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update role";
      toast.error(message);
    } finally {
      setSavingRoleId((prev) => (prev === roleId ? null : prev));
    }
  };

  const toggleUserRole = async (user: ApiUser, roleId: string) => {
    const currentRoleIds = new Set(user.roles.map((role) => role.id));
    if (currentRoleIds.has(roleId)) {
      currentRoleIds.delete(roleId);
    } else {
      currentRoleIds.add(roleId);
    }

    try {
      const response = await authenticatedRequest<{ user: ApiUser }>(
        `/api/v1/auth/users/${user.id}/roles`,
        {
          method: "PATCH",
          body: JSON.stringify({ roleIds: Array.from(currentRoleIds) }),
        }
      );
      setUsers((prev) =>
        prev.map((item) => (item.id === user.id ? response.user : item))
      );
      toast.success("Roles updated");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update roles";
      toast.error(message);
    }
  };

  const submitNewUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newUserEmail) {
      toast.error("Email is required");
      return;
    }

    setCreating(true);
    try {
      const response = await authenticatedRequest<{ user: ApiUser }>(
        "/api/v1/auth/users",
        {
          method: "POST",
          body: JSON.stringify({
            email: newUserEmail.trim(),
            name: newUserName.trim() || undefined,
            roleIds: newUserRoleIds,
            password: newUserPassword.trim() || undefined,
          }),
        }
      );

      setUsers((prev) => [response.user, ...prev]);
      setNewUserEmail("");
      setNewUserName("");
      setNewUserRoleIds([]);
      setNewUserPassword("");
      toast.success(
        newUserPassword
          ? "User created. Share the password you set with them."
          : "User created. Ask them to reset their password via the login screen."
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create user";
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const submitNewAction = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newActionKey.trim() || !newActionDescription.trim()) {
      toast.error("Permission key and description are required");
      return;
    }

    setCreatingAction(true);
    try {
      const response = await authenticatedRequest<{ action: ApiAction }>(
        "/api/v1/access-control/actions",
        {
          method: "POST",
          body: JSON.stringify({
            key: newActionKey.trim(),
            description: newActionDescription.trim(),
          }),
        }
      );

      setActions((prev) =>
        [...prev, response.action].sort((a, b) =>
          a.key.localeCompare(b.key, undefined, { sensitivity: "base" })
        )
      );
      setNewActionKey("");
      setNewActionDescription("");
      toast.success("Permission created");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create permission";
      toast.error(message);
    } finally {
      setCreatingAction(false);
    }
  };

  const submitNewRole = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newRoleName.trim()) {
      toast.error("Role name is required");
      return;
    }

    setCreatingRole(true);
    try {
      const response = await authenticatedRequest<{ role: ApiRole }>(
        "/api/v1/access-control/roles",
        {
          method: "POST",
          body: JSON.stringify({
            name: newRoleName.trim(),
            description: newRoleDescription.trim() || undefined,
            actionKeys: newRoleActionKeys,
          }),
        }
      );

      setRoles((prev) =>
        [...prev, response.role].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        )
      );
      setNewRoleName("");
      setNewRoleDescription("");
      setNewRoleActionKeys([]);
      toast.success("Role created");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create role";
      toast.error(message);
    } finally {
      setCreatingRole(false);
    }
  };

  const submitNewPccAccount = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newPccEmail || !newPccTeamMemberId) {
      toast.error("Email and team member are required");
      return;
    }

    setCreatingPcc(true);
    try {
      await authenticatedRequest<{ sent: boolean }>(
        "/api/v1/team-members/pcc-accounts/invite",
        {
          method: "POST",
          body: JSON.stringify({
            email: newPccEmail.trim(),
            teamMemberId: newPccTeamMemberId,
          }),
        }
      );
      setNewPccEmail("");
      setNewPccTeamMemberId("");
      toast.success(`Invite sent to ${newPccEmail.trim()} — they'll receive an email to set their password.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to send invite";
      toast.error(message);
    } finally {
      setCreatingPcc(false);
    }
  };

  const deletePccAccount = async (accountId: string) => {
    setDeletingPccId(accountId);
    try {
      await authenticatedRequest<void>(
        `/api/v1/access-control/pcc-accounts/${accountId}`,
        { method: "DELETE" }
      );
      setPccAccounts((prev) => prev.filter((a) => a.id !== accountId));
      toast.success("PCC account removed");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to remove PCC account";
      toast.error(message);
    } finally {
      setDeletingPccId(null);
    }
  };

  const deleteUser = async (userId: string) => {
    setDeletingUserId(userId);
    try {
      await authenticatedRequest<void>(`/api/v1/auth/users/${userId}`, { method: "DELETE" });
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      toast.success("User deleted");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to delete user";
      toast.error(message);
    } finally {
      setDeletingUserId(null);
      setConfirmDeleteUserId(null);
    }
  };

  const submitLinkUser = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!linkUserId || !linkTeamMemberId) {
      toast.error("Select both a user and a team member");
      return;
    }
    setLinkingUser(true);
    try {
      const response = await authenticatedRequest<{ account: PccAccount }>(
        "/api/v1/access-control/pcc-accounts/link",
        {
          method: "POST",
          body: JSON.stringify({ userId: linkUserId, teamMemberId: linkTeamMemberId }),
        }
      );
      setPccAccounts((prev) => {
        const exists = prev.some((a) => a.id === response.account.id);
        return exists
          ? prev.map((a) => (a.id === response.account.id ? response.account : a))
          : [response.account, ...prev];
      });
      setLinkUserId("");
      setLinkTeamMemberId("");
      toast.success("User linked to team member — pcc role added to their existing roles");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to link user");
    } finally {
      setLinkingUser(false);
    }
  };

  const unlinkUser = async (userId: string) => {
    setUnlinkingUserId(userId);
    try {
      await authenticatedRequest<void>(
        `/api/v1/access-control/pcc-accounts/link/${userId}`,
        { method: "DELETE" }
      );
      setPccAccounts((prev) => prev.filter((a) => a.id !== userId));
      toast.success("Team member link removed");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to unlink user");
    } finally {
      setUnlinkingUserId(null);
    }
  };

  if (!allowed) {
    return (
      <div className="max-w-3xl mx-auto py-10 text-center">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-100 text-red-600 mb-4">
          <Shield className="h-8 w-8" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-800">
          Access restricted
        </h2>
        <p className="mt-2 text-gray-600">
          You do not have permission to manage users and roles. Contact your
          administrator if you believe this is a mistake.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-500" /> Access Control
          </h1>
          <p className="text-sm text-gray-600">
            Manage platform users, roles, and the permissions assigned to those
            roles.
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing || loading}
          className="inline-flex items-center rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className="h-4 w-4 mr-2" /> Reload
        </button>
      </header>

      <div className={isAdmin ? "grid gap-6 lg:grid-cols-2" : ""}>
        <section className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-800">
              <UsersIcon className="h-5 w-5 text-blue-500" /> Platform Users
            </h2>
            <span className="text-sm text-gray-500">{users.length} total</span>
          </div>

          {loading ? (
            <p className="text-gray-500 text-sm">Loading users...</p>
          ) : users.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No users found. Add a new user with the form below.
            </p>
          ) : (
            <ul className="space-y-4">
              {users.map((user) => (
                <li
                  key={user.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {user.displayName || user.email}
                      </p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {user.roles.map((role) => (
                          <span
                            key={role.id}
                            className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-xs font-medium text-blue-700"
                          >
                            <Key className="h-3 w-3 mr-1" />
                            {role.name}
                          </span>
                        ))}
                        {user.roles.length === 0 && (
                          <span className="text-xs text-gray-500">
                            No roles assigned
                          </span>
                        )}
                      </div>
                      <div className="mt-3 text-xs text-gray-500 space-y-1">
                        {user.lastLoginAt && (
                          <p>
                            Last login: {new Date(user.lastLoginAt).toLocaleString()}
                          </p>
                        )}
                        {user.createdAt && (
                          <p>
                            Created: {new Date(user.createdAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <button
                        onClick={() =>
                          setEditingUserId((prev) =>
                            prev === user.id ? null : user.id
                          )
                        }
                        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                      >
                        {editingUserId === user.id ? (
                          <>
                            <X className="h-4 w-4 mr-1" /> Done
                          </>
                        ) : (
                          <>
                            <Key className="h-4 w-4 mr-1" /> Manage roles
                          </>
                        )}
                      </button>
                      {confirmDeleteUserId === user.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-red-600 font-medium">Delete permanently?</span>
                          <button
                            onClick={() => deleteUser(user.id)}
                            disabled={deletingUserId === user.id}
                            className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            {deletingUserId === user.id ? "Deleting..." : "Yes, delete"}
                          </button>
                          <button
                            onClick={() => setConfirmDeleteUserId(null)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (() => {
                          const targetIsAdmin = user.roles.some((r) => r.name === "admin");
                          if (targetIsAdmin && !isAdmin) return null;
                          return (
                            <button
                              onClick={() => setConfirmDeleteUserId(user.id)}
                              className="inline-flex items-center text-sm text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4 mr-1" /> Delete user
                            </button>
                          );
                        })()}
                    </div>
                  </div>

                  {editingUserId === user.id && (
                    <div className="mt-4 border-t pt-4">
                      <p className="text-xs text-gray-500 mb-3">
                        Select the roles that should be assigned to this user.
                      </p>
                      <div className="grid gap-2 md:grid-cols-2">
                        {roles.map((role) => {
                          const hasRole = user.roles.some((r) => r.id === role.id);
                          return (
                            <label
                              key={role.id}
                              className="flex items-start gap-2 rounded border border-gray-200 p-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={hasRole}
                                onChange={() => toggleUserRole(user, role.id)}
                              />
                              <span>
                                <span className="font-medium text-gray-800">
                                  {role.name}
                                </span>
                                {role.description && (
                                  <span className="block text-xs text-gray-500">
                                    {role.description}
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {isAdmin && <section className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-500" /> Roles & Permissions
            </h2>
            <span className="text-sm text-gray-500">{roles.length} roles</span>
          </div>

          {loading ? (
            <p className="text-gray-500 text-sm">Loading roles...</p>
          ) : roles.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No roles defined. Seed data should include admin, billing-manager,
              and viewer roles.
            </p>
          ) : (
            <ul className="space-y-4">
              {roles.map((role) => (
                <li key={role.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {role.name}
                      </p>
                      {role.description && (
                        <p className="text-sm text-gray-600">{role.description}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-xs text-gray-500">
                        {role.actions.length} permissions
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          editingRoleId === role.id
                            ? cancelEditingRole()
                            : startEditingRole(role)
                        }
                        className="inline-flex items-center text-xs font-medium text-blue-600 hover:text-blue-800"
                      >
                        {editingRoleId === role.id ? "Cancel" : "Edit role"}
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {role.actions.map((action) => (
                      <span
                        key={action.id}
                        className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-xs text-gray-700"
                      >
                        <Check className="h-3 w-3 mr-1 text-green-600" />
                        {actionsByKey.get(action.key)?.description || action.key}
                      </span>
                    ))}
                    {role.actions.length === 0 && (
                      <span className="text-xs text-gray-500">
                        No permissions assigned
                      </span>
                    )}
                  </div>
                  {editingRoleId === role.id && (
                    <div className="mt-4 border-t pt-4 space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                          Description
                        </label>
                        <input
                          type="text"
                          value={editingRoleDescription}
                          onChange={(event) =>
                            setEditingRoleDescription(event.target.value)
                          }
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                          placeholder="Optional role description"
                        />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                          Update permissions
                        </p>
                        {actions.length === 0 ? (
                          <p className="text-xs text-gray-500 mt-2">
                            No permissions available.
                          </p>
                        ) : (
                          <div className="mt-2 grid gap-2 md:grid-cols-2">
                            {actions.map((action) => {
                              const checked =
                                editingRoleActionKeys.includes(action.key);
                              return (
                                <label
                                  key={action.id}
                                  className="flex items-start gap-2 rounded border border-gray-200 p-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  <input
                                    type="checkbox"
                                    className="mt-0.5"
                                    checked={checked}
                                    onChange={() =>
                                      toggleEditingRoleAction(action.key)
                                    }
                                  />
                                  <span>
                                    <span className="font-medium text-gray-800">
                                      {action.key}
                                    </span>
                                    <span className="block text-xs text-gray-500">
                                      {action.description}
                                    </span>
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => saveRoleUpdates(role.id)}
                          disabled={savingRoleId === role.id}
                          className="inline-flex items-center px-3 py-2 rounded-md bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50"
                        >
                          {savingRoleId === role.id ? "Saving..." : "Save changes"}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditingRole}
                          className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-6 border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Create permission
            </h3>
            <form
              onSubmit={submitNewAction}
              className="grid gap-3 md:grid-cols-2 items-end"
            >
              <div>
                <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Key
                </label>
                <input
                  type="text"
                  value={newActionKey}
                  onChange={(event) => setNewActionKey(event.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  placeholder="billing.run"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Description
                </label>
                <input
                  type="text"
                  value={newActionDescription}
                  onChange={(event) => setNewActionDescription(event.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  placeholder="Can trigger billing runs"
                  required
                />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button
                  type="submit"
                  disabled={creatingAction}
                  className="inline-flex items-center px-3 py-2 rounded-md bg-slate-700 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
                >
                  {creatingAction ? "Creating..." : "Save permission"}
                </button>
              </div>
            </form>
          </div>

          <div className="mt-6 border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Create role
            </h3>
            <form onSubmit={submitNewRole} className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newRoleName}
                    onChange={(event) => setNewRoleName(event.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="billing-operator"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newRoleDescription}
                    onChange={(event) => setNewRoleDescription(event.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="Can run billing but not edit settings"
                  />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  Assign permissions
                </p>
                {actions.length === 0 ? (
                  <p className="text-xs text-gray-500 mt-2">
                    Create permissions first, then assign them to this role.
                  </p>
                ) : (
                  <div className="mt-2 grid gap-2 md:grid-cols-2">
                    {actions.map((action) => {
                      const checked = newRoleActionKeys.includes(action.key);
                      return (
                        <label
                          key={action.id}
                          className="flex items-start gap-2 rounded border border-gray-200 p-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={checked}
                            onChange={() => toggleNewRoleAction(action.key)}
                          />
                          <span>
                            <span className="font-medium text-gray-800">
                              {action.key}
                            </span>
                            <span className="block text-xs text-gray-500">
                              {action.description}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={creatingRole}
                  className="inline-flex items-center px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                >
                  {creatingRole ? "Creating..." : "Save role"}
                </button>
              </div>
            </form>
          </div>
        </section>}
      </div>

      <section className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
          <Plus className="h-5 w-5 text-green-500" /> Add New User
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Create an account manually, assign roles, and optionally set an initial
          password for the teammate.
        </p>

        <form onSubmit={submitNewUser} className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              required
              value={newUserEmail}
              onChange={(event) => setNewUserEmail(event.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              placeholder="user@example.com"
            />
          </div>

          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700">
              Display name (optional)
            </label>
            <input
              type="text"
              value={newUserName}
              onChange={(event) => setNewUserName(event.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              placeholder="Taylor Manager"
            />
          </div>

          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700">
              Temporary password (optional)
            </label>
            <input
              type="password"
              value={newUserPassword}
              onChange={(event) => setNewUserPassword(event.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              placeholder="Leave blank to auto-generate"
              minLength={8}
            />
            <p className="text-xs text-gray-500 mt-1">
              Share the password securely after creating the account.
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="block text-sm font-medium text-gray-700">
              Assign roles
            </p>
            <div className="mt-2 grid gap-2 md:grid-cols-3">
              {roles.map((role) => {
                const checked = newUserRoleIds.includes(role.id);
                return (
                  <label
                    key={role.id}
                    className="flex items-start gap-2 rounded border border-gray-200 p-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      onChange={(event) => {
                        setNewUserRoleIds((prev) => {
                          if (event.target.checked) {
                            return [...prev, role.id];
                          }
                          return prev.filter((id) => id !== role.id);
                        });
                      }}
                    />
                    <span>
                      <span className="font-medium text-gray-800">
                        {role.name}
                      </span>
                      {role.description && (
                        <span className="block text-xs text-gray-500">
                          {role.description}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-3 flex items-center justify-end">
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {creating ? "Creating..." : "Create user"}
            </button>
          </div>
        </form>
      </section>

      <section className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-1">
          <UserCheck className="h-5 w-5 text-indigo-500" /> PCC / Team Member Accounts
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Send an invite email to a PCC. They'll receive a link to set their own password — no credentials are shared manually.
        </p>

        <form onSubmit={submitNewPccAccount} className="grid gap-4 md:grid-cols-2 items-end mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">PCC Email</label>
            <input
              type="email"
              required
              value={newPccEmail}
              onChange={(e) => setNewPccEmail(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              placeholder="pcc@nrmedical.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Team Member</label>
            <SearchableSelect
              required
              value={newPccTeamMemberId}
              onChange={setNewPccTeamMemberId}
              placeholder="Select a team member..."
              options={teamMembers
                .filter((m) => !pccAccounts.some((a) => a.teamMemberId === m.id))
                .map((m) => ({
                  value: m.id,
                  label: `${m.firstName} ${m.lastName}${m.role ? ` — ${m.role}` : ""}`,
                }))}
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={creatingPcc}
              className="inline-flex items-center px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              <Send className="h-4 w-4 mr-1" />
              {creatingPcc ? "Sending invite..." : "Send invite email"}
            </button>
          </div>
        </form>

        <div className="border-t pt-5 mt-2">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Link existing platform user</h3>
          <p className="text-sm text-gray-500 mb-4">
            For managers or other users who already have a platform account and also need to clock in/out.
            Their existing roles are preserved — only the <code className="bg-gray-100 px-1 rounded text-xs">pcc</code> role is added.
          </p>
          <form onSubmit={submitLinkUser} className="grid gap-4 md:grid-cols-3 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700">Platform user</label>
              <SearchableSelect
                required
                value={linkUserId}
                onChange={setLinkUserId}
                placeholder="Select a user..."
                options={unlinkableUsers.map((u) => ({
                  value: u.id,
                  label: `${u.displayName || u.email} (${u.roles.map((r) => r.name).join(", ") || "no roles"})`,
                }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Team member</label>
              <SearchableSelect
                required
                value={linkTeamMemberId}
                onChange={setLinkTeamMemberId}
                placeholder="Select a team member..."
                options={unlinkedTeamMembers.map((m) => ({
                  value: m.id,
                  label: `${m.firstName} ${m.lastName}${m.role ? ` — ${m.role}` : ""}`,
                }))}
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={linkingUser}
                className="inline-flex items-center px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
              >
                <UserCheck className="h-4 w-4 mr-1" />
                {linkingUser ? "Linking..." : "Link user"}
              </button>
            </div>
          </form>
        </div>

        {loading ? (
          <p className="text-gray-500 text-sm">Loading PCC accounts...</p>
        ) : pccAccounts.length === 0 ? (
          <p className="text-gray-500 text-sm">No PCC accounts created yet.</p>
        ) : (
          <ul className="space-y-3">
            {pccAccounts.map((account) => {
              const member = teamMembers.find((m) => m.id === account.teamMemberId);
              const platformUser = users.find((u) => u.id === account.id);
              const isLinkedPlatformUser = platformUser != null && platformUser.roles.some((r) => r.name !== "pcc");
              return (
                <li key={account.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900">{account.email}</p>
                      {isLinkedPlatformUser && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-teal-50 text-xs font-medium text-teal-700">
                          platform user
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Linked to: {member ? `${member.firstName} ${member.lastName}` : account.teamMemberId}
                    </p>
                    {isLinkedPlatformUser && platformUser && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Roles: {platformUser.roles.map((r) => r.name).join(", ")}
                      </p>
                    )}
                  </div>
                  {isLinkedPlatformUser ? (
                    <button
                      type="button"
                      onClick={() => unlinkUser(account.id)}
                      disabled={unlinkingUserId === account.id}
                      className="inline-flex items-center text-sm text-orange-600 hover:text-orange-800 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {unlinkingUserId === account.id ? "Unlinking..." : "Unlink"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => deletePccAccount(account.id)}
                      disabled={deletingPccId === account.id}
                      className="inline-flex items-center text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {deletingPccId === account.id ? "Removing..." : "Remove"}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
